import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { runOnJS, useAnimatedReaction, } from 'react-native-reanimated';
import { rem } from 'design-system';
import SelectorControllableReanimated from 'design-system/SelectorControllableReanimated';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { isWeb } from '../../../../constants';
const TABS_SPACE_BETWEEN = rem(4);
const TAB_PADDING_HORIZONTAL = rem(16);
const TAB_WIDTHS = {
    Everyday: rem(120),
    Savings: rem(108),
    Investments: rem(140),
    Assets: rem(98),
    Debt: rem(80),
};
const DEFAULT_TAB_WIDTH = rem(120);
const DetailScreenPageControl = ({ tabs, scrollX, carouselRef, scrollRef, onChange, }) => {
    const { width, contentWidth, paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const sections = useMemo(() => tabs.map((tab) => ({
        id: tab.label,
        text: `${tab.label} (${tab.count})`,
        width: TAB_WIDTHS[tab.label] ?? DEFAULT_TAB_WIDTH,
    })), [tabs]);
    // Cumulative left-edge offsets for each tab (excluding padding)
    const tabOffsets = useMemo(() => {
        const offsets = [];
        let offset = 0;
        for (const tab of tabs) {
            offsets.push(offset);
            offset +=
                (TAB_WIDTHS[tab.label] ?? DEFAULT_TAB_WIDTH) + TABS_SPACE_BETWEEN;
        }
        return offsets;
    }, [tabs]);
    const scrollTabToCenter = useCallback((index) => {
        const tabOffset = tabOffsets[index] ?? 0;
        const tabWidth = TAB_WIDTHS[tabs[index]?.label ?? ''] ?? DEFAULT_TAB_WIDTH;
        const x = TAB_PADDING_HORIZONTAL + tabOffset + tabWidth / 2 - width / 2;
        scrollRef.current?.scrollTo({ x: Math.max(0, x), animated: true });
    }, [tabOffsets, tabs, width, scrollRef]);
    // Scroll tab bar when carousel page changes
    useAnimatedReaction(() => Math.round(scrollX.value / contentWidth), (current, previous) => {
        if (current !== previous && current >= 0 && current < tabs.length) {
            runOnJS(scrollTabToCenter)(current);
        }
    }, [contentWidth, tabs.length, scrollTabToCenter]);
    // Scroll to initial tab on mount
    useEffect(() => {
        const initialIndex = Math.round(scrollX.value / contentWidth);
        if (initialIndex > 0) {
            scrollTabToCenter(initialIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const setSelection = useCallback((index) => {
        if (isWeb) {
            // eslint-disable-next-line no-underscore-dangle
            carouselRef.current?._snapToItem(index, true, true, true);
        }
        else {
            carouselRef.current?.snapToItem(index, true, true);
        }
        onChange(tabs[index].label);
    }, [carouselRef, onChange, tabs]);
    return (<ScrollView horizontal scrollEnabled ref={scrollRef} style={styles.container} showsHorizontalScrollIndicator={false} contentContainerStyle={[
            styles.contentContainerStyle,
            paddingHorizontalStyle,
        ]}>
      <SelectorControllableReanimated scrollX={scrollX} sections={sections} screenWidth={contentWidth} spaceBetween={TABS_SPACE_BETWEEN} setSelection={setSelection} itemStyle={styles.itemStyle} selectorStyle={styles.selectorStyle}/>
    </ScrollView>);
};
export default memo(DetailScreenPageControl);
const styles = StyleSheet.create({
    container: {
        marginBottom: rem(16),
        height: rem(34),
        maxHeight: rem(34),
    },
    contentContainerStyle: {
        paddingHorizontal: rem(16),
    },
    selectorStyle: {
        borderRadius: rem(8),
        justifyContent: 'center',
    },
    itemStyle: {
        paddingVertical: 0,
    },
});
